import {
  SYNC_RULES,
  activeRules,
  catalogueEntry,
  mappedCount,
  type CatalogueEntry,
  type ExtensionId,
  type IntegrationInstance,
  type IntegrationState,
  type RunState,
  type SyncRule,
  type SyncRules,
} from '@forecastify/shared';
import { Badge, Icon, type BadgeVariant, type IconName } from '@forecastify/ui';
import type { MessageKey, Translate } from '../../i18n';
import { ProviderLogo, hasLogo } from './provider-logo';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ C33 · The vocabulary three integration screens share.                     │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * The catalogue list, the instance detail and the wizard all name a state, translate a rule and
 * count a map. Each of those is one line of logic and three chances to disagree — the mockup keeps
 * them as `igStateLabel`, `igStateVariant`, `igRunLabel` and a handful of inline expressions, and
 * this is the same set with the strings taken out of it.
 */

/**
 * Colour and label for an instance's state. The mockup's `igStateLabel` / `igStateVariant`.
 *
 * ⚠️⚠️ **THE DOT IS A TOKEN, NOT A HEX.** The mockup hard-codes `#209292` and friends; the lint rule
 * calls a literal *"a ninth theme that applies to one element"*, and it is right — this product has
 * eight, and a dot that stays teal in all of them is the one thing on the row that ignores the
 * theme. `--status-ok` and its siblings are exactly this vocabulary.
 */
export function stateBadge(state: IntegrationState): {
  key: MessageKey;
  variant: BadgeVariant;
  dot: string;
} {
  if (state === 'active') {
    return { key: 'integrations.state.active', variant: 'default', dot: 'var(--status-ok)' };
  }
  if (state === 'paused') {
    return { key: 'integrations.state.paused', variant: 'secondary', dot: 'var(--status-idle)' };
  }
  if (state === 'error') {
    return { key: 'integrations.state.error', variant: 'destructive', dot: 'var(--status-bad)' };
  }
  return { key: 'integrations.state.setup', variant: 'outline', dot: 'var(--status-warn)' };
}

export function runBadge(state: RunState): { key: MessageKey; variant: BadgeVariant } {
  if (state === 'ok') return { key: 'integrations.run.state.ok', variant: 'secondary' };
  if (state === 'partial') return { key: 'integrations.run.state.partial', variant: 'outline' };
  if (state === 'error') return { key: 'integrations.run.state.error', variant: 'destructive' };
  return { key: 'integrations.run.state.running', variant: 'default' };
}

/**
 * ⚠️ **A `Record`, not a switch with a fall-through.** C30 shipped an `entityLabel` that was five
 * `if`s ending in a default, and adding two metafield entities made both render as *Worklog* with a
 * green typecheck. A total record over the union is a type error the day a rule is added.
 */
const RULE_LABEL: Record<SyncRule, MessageKey> = {
  taskOut: 'integrations.rule.taskOut',
  statusBoth: 'integrations.rule.statusBoth',
};

const RULE_DESC: Record<SyncRule, MessageKey> = {
  taskOut: 'integrations.rule.taskOut.desc',
  statusBoth: 'integrations.rule.statusBoth.desc',
};

export const ruleLabel = (t: Translate, rule: SyncRule): string => t(RULE_LABEL[rule]);
export const ruleDesc = (t: Translate, rule: SyncRule): string => t(RULE_DESC[rule]);

/** The mockup's chip row on the overview: every rule, the off ones dashed. */
export function RuleChips({ t, rules }: { readonly t: Translate; readonly rules: SyncRules }) {
  return (
    <div className="mt-2 flex flex-wrap gap-1.5">
      {SYNC_RULES.map((rule) => (
        <span
          key={rule}
          className={[
            'flex items-center gap-1.5 rounded-md border px-2 py-1 text-[11.5px]',
            rules[rule]
              ? 'border-primary/40 bg-primary/5 text-foreground'
              : 'text-muted-foreground border-dashed',
          ].join(' ')}
        >
          {ruleLabel(t, rule)}
        </span>
      ))}
    </div>
  );
}

/**
 * ⚠️⚠️ **A RUN ERROR IS `code:detail`, AND THIS IS WHERE IT BECOMES A SENTENCE.** `0060` and
 * `sync-stub.ts` argue the encoding: a Hungarian string in a `text[]` is one no English reader can
 * read. **The fallback prints the raw value** rather than swallowing it, which is what makes room
 * for C34 passing a provider's own message through verbatim — a 403 from Jira is not something we
 * will have a key for, and hiding it would be worse than showing it untranslated.
 */
export function runErrorText(t: Translate, raw: string): string {
  const split = raw.indexOf(':');
  const code = split === -1 ? raw : raw.slice(0, split);
  const detail = split === -1 ? '' : raw.slice(split + 1);
  if (code === 'unmapped_status') return t('integrations.error.unmapped_status', { detail });
  if (code === 'unmapped_user') return t('integrations.error.unmapped_user', { detail });
  if (code === 'no_rules_enabled') return t('integrations.error.no_rules_enabled');
  /* ⚠️ The four lines the new run kinds write — see `run-log.ts`. Each one is a fact about a run
     that moved nothing, which without a sentence reads as a run that failed. */
  if (code === 'discovered_projects') return t('integrations.run.discoveredProjects', { detail });
  if (code === 'discovered_people') return t('integrations.run.discoveredPeople', { detail });
  if (code === 'project_unchanged') return t('integrations.run.projectUnchanged', { detail });
  if (code === 'restatus_awaiting_reread') return t('integrations.run.awaitingReread', { detail });
  if (code === 'webhook_ignored') return t('integrations.run.webhookIgnored', { detail });
  if (code === 'webhook_duplicate') return t('integrations.run.webhookDuplicate', { detail });
  return raw;
}

const ENTITY_LABEL: Record<string, MessageKey> = {
  task: 'integrations.run.entity.task',
  worklog: 'integrations.run.entity.worklog',
  comment: 'integrations.run.entity.comment',
};

const ACTION_LABEL: Record<string, MessageKey> = {
  created: 'integrations.run.action.created',
  updated: 'integrations.run.action.updated',
  skipped: 'integrations.run.action.skipped',
};

/**
 * ⚠️ **The same "print what you were given" fallback as the errors, and for the same reason.**
 * `0060` keeps `entity` and `action` as free text precisely so a provider can report something we
 * have no word for; a run log that dropped such a line would lose exactly what somebody is reading
 * it for.
 */
export const entityLabel = (t: Translate, entity: string): string => {
  const key = ENTITY_LABEL[entity];
  return key === undefined ? entity : t(key);
};

export const actionLabel = (t: Translate, action: string): string => {
  const key = ACTION_LABEL[action];
  return key === undefined ? action : t(key);
};

/** The note column. Today the stub is the only writer, so this is a key; C34 adds real ones. */
export const noteText = (t: Translate, note: string): string =>
  note === 'stub' ? t('integrations.note.stub') : note;

/** The provider tile — the mockup's `background: <col>1f; color: <col>`. */
export function providerTile(entry: CatalogueEntry): { background: string; color: string } {
  return { background: `${entry.color}1f`, color: entry.color };
}

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ The tracker's own mark, on every extension and every instance - item 18.
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **IT DREW A LUCIDE GLYPH IN THE VENDOR'S COLOUR, WHICH IS A SILHOUETTE AND NOT A LOGO.**
 * `entry.icon` is a name in an icon set - one stroke weight, `currentColor` throughout - so Jira
 * was a generic tick and YouTrack a generic bolt, tinted. Item 18 asked for the real marks, and
 * D9 supplied them; `provider-logo.tsx` brought them into the app in batch D for the person rail
 * and the instance header, and this is the same artwork reaching the two surfaces that still had
 * the silhouette.
 *
 * ⚠️ **The coloured tile STAYS behind it.** It is the mockup's own shape, it is what gives a
 * 40px card cell something to sit in, and both vendor marks are drawn to read on a light ground -
 * which a 12%-alpha tile in the vendor's own colour is.
 *
 * ⚠️ **A provider with no artwork falls back to the glyph rather than to a gap.** There are two
 * entries in the catalogue today and a file for each; the fallback is what stops a third one
 * shipping an empty tile before anybody notices the asset is missing.
 */
export function ProviderIcon({
  entry,
  size,
  className,
}: {
  readonly entry: CatalogueEntry;
  readonly size: number;
  readonly className?: string;
}) {
  return (
    <span
      className={className ?? 'grid size-11 shrink-0 place-items-center rounded-xl'}
      style={providerTile(entry)}
    >
      <ProviderLogo provider={entry.id} size={size} />
      {hasLogo(entry.id) ? null : <Icon name={entry.icon as IconName} size={size} />}
    </span>
  );
}

export function StateBadge({
  t,
  state,
}: {
  readonly t: Translate;
  readonly state: IntegrationState;
}) {
  const badge = stateBadge(state);
  return <Badge variant={badge.variant}>{t(badge.key)}</Badge>;
}

/** The overview's two map cards and the settings tab's summary lines both need this pair. */
export function mapSummary(
  t: Translate,
  instance: IntegrationInstance,
): readonly {
  key: 'statuses' | 'priorities' | 'people' | 'projects';
  icon: IconName;
  label: string;
  value: string;
  note: string;
  warn: boolean;
}[] {
  const statuses = mappedCount(instance.statuses);
  const priorities = mappedCount(instance.priorities);
  const users = mappedCount(instance.users);
  return [
    {
      key: 'statuses',
      icon: 'circle-dot',
      label: t('integrations.map.statuses'),
      value: t('integrations.map.count', statuses),
      note: t(statuses.mapped === statuses.total ? 'integrations.map.ok' : 'integrations.map.warn'),
      warn: statuses.mapped !== statuses.total,
    },
    /**
     * J-5 · the priority map, ranked like the status map.
     *
     * ⚠️ **Its unmapped note says something softer**, because an unmapped priority leaves nothing
     * out: the issue still arrives, with Forecastify's own priority. The warning is the same amber,
     * since a half-mapped list is still a step somebody did not finish.
     */
    {
      key: 'priorities',
      icon: 'flag',
      label: t('integrations.map.priorities'),
      value: t('integrations.map.count', priorities),
      note: t(
        priorities.mapped === priorities.total
          ? 'integrations.map.ok'
          : 'integrations.map.prioritiesWarn',
      ),
      warn: priorities.mapped !== priorities.total,
    },
    {
      key: 'people',
      icon: 'user-round',
      label: t('integrations.map.people'),
      value: t('integrations.map.count', users),
      note: t(users.mapped === users.total ? 'integrations.map.ok' : 'integrations.map.warn'),
      warn: users.mapped !== users.total,
    },
    /**
     * ⚠️⚠️ **THE THIRD CARD, AND IT IS THE ONE THAT DECIDES WHETHER ANYTHING SYNCS AT ALL — item
     * 67.** Statuses and people were ranked and projects were not, so an instance with a green
     * connection, every status mapped and every person paired could still be linked to nothing —
     * `overwatch.service.ts` writes an `unlinked_project` skip per project and moves on. A working
     * integration that will never do anything, with three reassuring cards above it.
     *
     * ⚠️ **Zero is the WARNING**, and it is the only threshold worth having here: one linked
     * project is a deliberate choice somebody made, and none is always a step nobody finished.
     */
    {
      key: 'projects',
      icon: 'folder',
      label: t('integrations.map.projects'),
      value: String(instance.linkedProjectCount),
      note: t(
        instance.linkedProjectCount === 0
          ? 'integrations.map.noProjects'
          : 'integrations.map.projectsOk',
      ),
      warn: instance.linkedProjectCount === 0,
    },
  ];
}

/** The provider a name belongs to, or null — the screens all resolve this the same way. */
export const providerOf = (instance: IntegrationInstance): CatalogueEntry | null =>
  catalogueEntry(instance.extensionId);

export { activeRules };

/**
 * An extension's name, in the reader's language.
 *
 * ⚠️ **It lived in `marketplace-screen.tsx` until item 68 deleted that file.** The copy is
 * `ext.<id>.name` in the i18n catalogue and the FACTS are `EXTENSION_CATALOGUE` in `shared` -
 * rule 7 admits no exception for a screen whose content happens to be about brands.
 */
export function extensionName(t: Translate, id: ExtensionId): string {
  return t(`ext.${id}.name` as MessageKey);
}
